'use client';
import React, { useState } from 'react';
import StatusBadge from '@/components/ui/StatusBadge';
import Icon from '@/components/ui/AppIcon';

interface BrandAccount {
  id: string;
  name: string;
  contact: string;
  role: string;
  email: string;
  sector: string;
  campaigns: number;
  spend: string;
  status: 'active' | 'pending' | 'paused';
  joinedDate: string;
  accessCode: string;
}

const initialBrands: BrandAccount[] = [
  { id: 'brand-samsung', name: 'Samsung Algérie', contact: 'Soudani Khedidja', role: 'General Manager', email: 's.khedidja@samsung.com', sector: 'Tech', campaigns: 4, spend: '6.2M DA', status: 'active', joinedDate: 'Jan 2026', accessCode: 'SAM-2026' },
  { id: 'brand-loreal', name: "L'Oréal Paris DZ", contact: 'Walid Sersoub', role: 'Brand Manager', email: 'w.sersoub@loreal.dz', sector: 'Beauty', campaigns: 8, spend: '12.4M DA', status: 'active', joinedDate: 'Oct 2025', accessCode: 'LOR-2025' },
  { id: 'brand-djezzy', name: 'Djezzy', contact: 'Kamel Boukhalfa', role: 'Brand Manager', email: 'k.boukhalfa@djezzy.dz', sector: 'Telecom', campaigns: 6, spend: '9.1M DA', status: 'active', joinedDate: 'Nov 2025', accessCode: 'DJZ-2025' },
  { id: 'brand-yassir', name: 'Yassir', contact: 'Amira Tahir', role: 'Brand Manager', email: 'a.tahir@yassir.io', sector: 'Tech', campaigns: 3, spend: '3.8M DA', status: 'active', joinedDate: 'Mar 2026', accessCode: 'YAS-2026' },
  { id: 'brand-tecno', name: 'TECNO Mobile', contact: 'Farid Ouahab', role: 'Brand Manager', email: 'f.ouahab@tecno.com', sector: 'Tech', campaigns: 2, spend: '2.1M DA', status: 'active', joinedDate: 'Avr 2026', accessCode: 'TEC-2026' },
  { id: 'brand-cocacola', name: 'Coca-Cola Algérie', contact: 'Nora Belhadj', role: 'Brand Manager', email: 'n.belhadj@coca-cola.dz', sector: 'FMCG', campaigns: 5, spend: '7.6M DA', status: 'active', joinedDate: 'Déc 2025', accessCode: 'CCA-2025' },
  { id: 'brand-garnier', name: 'Garnier DZ', contact: 'Lydia Mansouri', role: 'Brand Manager', email: 'l.mansouri@garnier.dz', sector: 'Beauty', campaigns: 4, spend: '4.9M DA', status: 'pending', joinedDate: 'Aoû 2026', accessCode: 'GAR-2026' },
  { id: 'brand-ooredoo', name: 'Ooredoo Algérie', contact: 'Rachid Ferhat', role: 'Brand Manager', email: 'r.ferhat@ooredoo.dz', sector: 'Telecom', campaigns: 1, spend: '1.2M DA', status: 'paused', joinedDate: 'Fév 2026', accessCode: 'OOR-2026' },
];

const emptyForm = {
  name: '',
  contact: '',
  role: 'Brand Manager',
  email: '',
  sector: 'Tech',
  accessCode: '',
  status: 'active\' as \'active\' | \'pending\' | \'paused',
};

export default function AdminBrandAccounts() {
  const [brands, setBrands] = useState<BrandAccount[]>(initialBrands);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editBrand, setEditBrand] = useState<BrandAccount | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [showCode, setShowCode] = useState<string | null>(null);
  const [form, setForm] = useState({ ...emptyForm });

  const filtered = brands.filter(b =>
    b.name.toLowerCase().includes(search.toLowerCase()) ||
    b.contact.toLowerCase().includes(search.toLowerCase())
  );

  function openCreate() {
    setForm({ ...emptyForm });
    setShowCreate(true);
  }

  function openEdit(brand: BrandAccount) {
    setForm({
      name: brand.name,
      contact: brand.contact,
      role: brand.role,
      email: brand.email,
      sector: brand.sector,
      accessCode: brand.accessCode,
      status: brand.status,
    });
    setEditBrand(brand);
  }

  function handleSaveCreate() {
    if (!form.name || !form.contact || !form.email || !form.accessCode) return;
    const newBrand: BrandAccount = {
      id: 'brand-' + Date.now(),
      name: form.name,
      contact: form.contact,
      role: form.role,
      email: form.email,
      sector: form.sector,
      campaigns: 0,
      spend: '0 DA',
      status: form.status,
      joinedDate: new Date().toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }),
      accessCode: form.accessCode,
    };
    setBrands(prev => [newBrand, ...prev]);
    setShowCreate(false);
  }

  function handleSaveEdit() {
    if (!editBrand) return;
    setBrands(prev => prev.map(b =>
      b.id === editBrand.id
        ? { ...b, name: form.name, contact: form.contact, role: form.role, email: form.email, sector: form.sector, accessCode: form.accessCode, status: form.status }
        : b
    ));
    setEditBrand(null);
  }

  function handleDelete(id: string) {
    setBrands(prev => prev.filter(b => b.id !== id));
    setDeleteId(null);
  }

  const sectors = ['Tech', 'Beauty', 'Telecom', 'FMCG', 'Food', 'Fashion', 'Automotive', 'Finance', 'Autre'];
  const roles = ['General Manager', 'Brand Manager', 'Marketing Manager', 'Digital Manager', 'Communication Manager'];

  const BrandFormModal = ({ title, onSave, onClose }: { title: string; onSave: () => void; onClose: () => void }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-card border border-border rounded-2xl w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h3 className="text-base font-700 text-foreground">{title}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-all">
            <Icon name="XMarkIcon" size={18} />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-xs font-600 text-muted-foreground mb-1.5">Nom de la marque *</label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Ex: Samsung Algérie"
                className="w-full bg-input border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder-muted-foreground outline-none focus:border-primary transition-colors"
              />
            </div>
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5">Nom du contact *</label>
              <input
                type="text"
                value={form.contact}
                onChange={e => setForm(f => ({ ...f, contact: e.target.value }))}
                placeholder="Ex: Soudani Khedidja"
                className="w-full bg-input border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder-muted-foreground outline-none focus:border-primary transition-colors"
              />
            </div>
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5">Rôle</label>
              <select
                value={form.role}
                onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                className="w-full bg-input border border-border rounded-lg px-3 py-2 text-sm text-foreground outline-none focus:border-primary transition-colors"
              >
                {roles.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5">Email *</label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="contact@marque.com"
                className="w-full bg-input border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder-muted-foreground outline-none focus:border-primary transition-colors"
              />
            </div>
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5">Secteur</label>
              <select
                value={form.sector}
                onChange={e => setForm(f => ({ ...f, sector: e.target.value }))}
                className="w-full bg-input border border-border rounded-lg px-3 py-2 text-sm text-foreground outline-none focus:border-primary transition-colors"
              >
                {sectors.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5">Statut</label>
              <select
                value={form.status}
                onChange={e => setForm(f => ({ ...f, status: e.target.value as 'active' | 'pending' | 'paused' }))}
                className="w-full bg-input border border-border rounded-lg px-3 py-2 text-sm text-foreground outline-none focus:border-primary transition-colors"
              >
                <option value="active">Actif</option>
                <option value="pending">En attente</option>
                <option value="paused">Suspendu</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-600 text-muted-foreground mb-1.5">Code d'accès marque *</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={form.accessCode}
                  onChange={e => setForm(f => ({ ...f, accessCode: e.target.value.toUpperCase() }))}
                  placeholder="Ex: SAM-2026"
                  className="flex-1 bg-input border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder-muted-foreground outline-none focus:border-primary transition-colors font-mono tracking-widest"
                />
                <button
                  type="button"
                  onClick={() => {
                    const code = Math.random().toString(36).substring(2, 5).toUpperCase() + '-' + new Date().getFullYear();
                    setForm(f => ({ ...f, accessCode: code }));
                  }}
                  className="px-3 py-2 bg-muted hover:bg-border text-foreground text-xs font-600 rounded-lg transition-all whitespace-nowrap"
                >
                  Générer
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Ce code permet à la marque d'accéder à son espace dédié.</p>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          <button onClick={onClose} className="px-4 py-2 text-sm font-600 text-muted-foreground hover:text-foreground transition-colors">
            Annuler
          </button>
          <button
            onClick={onSave}
            disabled={!form.name || !form.contact || !form.email || !form.accessCode}
            className="px-5 py-2 bg-primary text-primary-foreground text-sm font-700 rounded-lg hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-700 text-foreground">Comptes marques</h2>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2 focus-within:border-primary transition-colors">
            <Icon name="MagnifyingGlassIcon" size={14} className="text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher une marque..."
              className="bg-transparent text-sm text-foreground placeholder-muted-foreground outline-none w-40"
            />
          </div>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 bg-primary text-primary-foreground text-sm font-600 px-3 py-2 rounded-lg hover:opacity-90 transition-all"
          >
            <Icon name="PlusIcon" size={14} />
            Créer un compte
          </button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="px-4 py-3 text-left text-xs font-600 uppercase tracking-wide text-muted-foreground">Marque</th>
                <th className="px-4 py-3 text-left text-xs font-600 uppercase tracking-wide text-muted-foreground">Secteur</th>
                <th className="px-4 py-3 text-left text-xs font-600 uppercase tracking-wide text-muted-foreground">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-600 uppercase tracking-wide text-muted-foreground">Code d'accès</th>
                <th className="px-4 py-3 text-left text-xs font-600 uppercase tracking-wide text-muted-foreground">Campagnes</th>
                <th className="px-4 py-3 text-left text-xs font-600 uppercase tracking-wide text-muted-foreground">Budget total</th>
                <th className="px-4 py-3 text-left text-xs font-600 uppercase tracking-wide text-muted-foreground">Statut</th>
                <th className="px-4 py-3 text-left text-xs font-600 uppercase tracking-wide text-muted-foreground">Depuis</th>
                <th className="px-4 py-3 text-right text-xs font-600 uppercase tracking-wide text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((brand) => (
                <tr key={brand.id} className="group hover:bg-muted/40 transition-all">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-800 text-foreground">{brand.name.charAt(0)}</span>
                      </div>
                      <span className="text-sm font-700 text-foreground">{brand.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-muted-foreground">{brand.sector}</span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-xs font-600 text-foreground">{brand.contact}</p>
                    <p className="text-xs text-muted-foreground">{brand.role}</p>
                    <p className="text-xs text-muted-foreground">{brand.email}</p>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-mono font-700 text-foreground bg-muted px-2 py-0.5 rounded tracking-widest">
                        {showCode === brand.id ? brand.accessCode : '••••••••'}
                      </span>
                      <button
                        onClick={() => setShowCode(showCode === brand.id ? null : brand.id)}
                        className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-all"
                        title={showCode === brand.id ? 'Masquer' : 'Afficher le code'}
                      >
                        <Icon name={showCode === brand.id ? 'EyeSlashIcon' : 'EyeIcon'} size={12} />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-700 text-foreground font-mono">{brand.campaigns}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-600 text-foreground font-mono">{brand.spend}</span>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge variant={brand.status} size="sm" />
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-muted-foreground">{brand.joinedDate}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        onClick={() => openEdit(brand)}
                        className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-all"
                        title="Modifier"
                      >
                        <Icon name="PencilSquareIcon" size={14} />
                      </button>
                      <button
                        onClick={() => setDeleteId(brand.id)}
                        className="p-1.5 rounded-lg hover:bg-negative/10 text-muted-foreground hover:text-negative transition-all"
                        title="Supprimer"
                      >
                        <Icon name="TrashIcon" size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-sm text-muted-foreground">Aucune marque trouvée.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <BrandFormModal
          title="Créer un compte marque"
          onSave={handleSaveCreate}
          onClose={() => setShowCreate(false)}
        />
      )}

      {/* Edit Modal */}
      {editBrand && (
        <BrandFormModal
          title={`Modifier — ${editBrand.name}`}
          onSave={handleSaveEdit}
          onClose={() => setEditBrand(null)}
        />
      )}

      {/* Delete Confirm */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-card border border-border rounded-2xl w-full max-w-sm shadow-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-negative/10 flex items-center justify-center flex-shrink-0">
                <Icon name="TrashIcon" size={18} className="text-negative" />
              </div>
              <div>
                <h3 className="text-sm font-700 text-foreground">Supprimer ce compte marque ?</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Cette action est irréversible.</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3">
              <button onClick={() => setDeleteId(null)} className="px-4 py-2 text-sm font-600 text-muted-foreground hover:text-foreground transition-colors">
                Annuler
              </button>
              <button
                onClick={() => handleDelete(deleteId)}
                className="px-5 py-2 bg-negative text-white text-sm font-700 rounded-lg hover:opacity-90 transition-all"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}